/* eslint-disable @typescript-eslint/no-explicit-any */
import * as SliderPrimitive from '@radix-ui/react-slider';

import { ColorOrCustom, cn, resolveColorClass } from 'styles';
import { ChangeEventLike, buildChangeEventLike } from './types';
import { getFirst } from 'utils/array';

type TypeProps =
  | {
      type?: 'single';
      value?: number;
      onChangeDirect?: (value: number) => void;
      onChange?: (value: ChangeEventLike<number>) => void;
    }
  | {
      type: 'range';
      value?: [number, number];
      onChangeDirect?: (value: number[]) => void;
      onChange?: (value: ChangeEventLike<number[]>) => void;
    };

type SliderProps = TypeProps & {
  className?: string;

  /**
   * The class applied to the selected section
   */
  rangeClassName?: string;

  /**
   * The class applied to the handle
   */
  thumbClassName?: string;

  size?: 'small' | 'medium';
  color?: ColorOrCustom;

  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  name?: string;
};

const sizeClasses = {
  small: {
    track: 'h-1',
    thumb: 'h-4 w-4',
  },

  medium: {
    track: 'h-1.5',
    thumb: 'h-5 w-5',
  },
};

export function Slider({
  className,
  size = 'medium',
  color = 'primary',
  onChange,
  onChangeDirect,
  value,
  min = 0,
  max = 100,
  step = 1,
  type = 'single',
  rangeClassName,
  thumbClassName,
  ...props
}: SliderProps) {
  const classes = sizeClasses[size];

  const handleChange = (numbers: number[]): void => {
    const newValue = type === 'single' ? getFirst(numbers) : numbers;

    const shouldStop =
      type === 'single' ? typeof newValue !== 'number' : (newValue as number[]).length < 2;

    if (shouldStop) return;

    onChangeDirect?.(newValue as any);
    onChange?.(
      buildChangeEventLike({
        name: props.name,
        id: props.name,
        value: newValue as any,
      }),
    );
  };

  return (
    <SliderPrimitive.Root
      className={cn(
        'relative flex w-full cursor-pointer touch-none select-none items-center',
        props.disabled && 'cursor-default',
        className,
      )}
      value={typeof value === 'number' ? [value] : value}
      onValueChange={handleChange}
      min={min}
      max={max}
      step={step}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'relative w-full grow overflow-hidden rounded-full bg-secondary',
          classes.track,
        )}
      >
        <SliderPrimitive.Range
          className={cn('absolute h-full', resolveColorClass(color, 'bg'), rangeClassName)}
        />
      </SliderPrimitive.Track>

      <SliderPrimitive.Thumb
        className={cn(
          'block rounded-full border-2 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          classes.thumb,
          resolveColorClass(color, 'bg'),
          resolveColorClass(color, 'border'),
          thumbClassName,
        )}
      />

      {type === 'range' && (
        <SliderPrimitive.Thumb
          className={cn(
            'block rounded-full border-2 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
            classes.thumb,
            resolveColorClass(color, 'bg'),
            resolveColorClass(color, 'border'),
            thumbClassName,
          )}
        />
      )}
    </SliderPrimitive.Root>
  );
}
